/**
 * 一站式请求配置工厂
 *
 * 将 auth、encrypt、error handling 整合为一个配置对象，
 * 直接用于 UmiJS request 插件。
 */

import { createUnauthorizedCodeChecker, DEFAULT_TIMEOUT } from './constants';
import { createErrorHandler, createErrorThrower } from './error-handler';
import {
  createAuthInterceptor,
  createDecryptResponseInterceptor,
  createEncryptRequestInterceptor,
} from './interceptors';
import type { RequestConfigOptions } from './types';

type RequestInterceptor = import('./types').RequestInterceptor;
type ResponseInterceptor = import('./types').ResponseInterceptor;

/** 已处理的未授权错误标记接口 */
interface UnauthorizedError extends Error {
  _unauthorizedHandled?: boolean;
}

/**
 * 创建完整的 UmiJS RequestConfig
 *
 * 一站式配置认证、加密、错误处理，直接导出给 UmiJS 使用。
 *
 * @param options - 配置选项
 * @returns UmiJS RequestConfig 对象
 *
 * @example
 * ```ts
 * // keel-cloud-web (无加密)
 * export const request = createRequestConfig({
 *   timeout: 10000,
 *   auth: {
 *     tokenKey: 'kc_auth_token',
 *     headerName: 'Authorization',
 *     headerPrefix: 'Bearer ',
 *   },
 *   errorConfig: {
 *     onUnauthorized: () => history.push('/login'),
 *     onError: (msg) => message.error(msg),
 *   },
 * });
 * ```
 */
export function createRequestConfig(options: RequestConfigOptions) {
  const {
    baseURL,
    timeout = DEFAULT_TIMEOUT,
    responseType,
    auth,
    encryption,
    requestInterceptors: customRequestInterceptors = [],
    responseInterceptors: customResponseInterceptors = [],
    errorConfig: errorCfg,
  } = options;
  const isUnauthorized = createUnauthorizedCodeChecker(
    errorCfg.unauthorizedCodes || [],
    errorCfg.includeDefaultUnauthorizedCodes ?? true,
  );

  // 构建请求拦截器
  const requestInterceptors: RequestInterceptor[] = [
    createAuthInterceptor(auth),
  ];

  if (encryption?.enabled && encryption.strategy) {
    requestInterceptors.push(createEncryptRequestInterceptor(encryption));
  }

  requestInterceptors.push(...customRequestInterceptors);

  // 响应拦截器
  const responseInterceptors: ResponseInterceptor[] = [];

  // 解密响应拦截器
  if (encryption?.enabled && encryption.strategy) {
    responseInterceptors.push(createDecryptResponseInterceptor(encryption));
  }
  responseInterceptors.push(...customResponseInterceptors);

  // 业务响应拦截器: 检查未授权状态码（提前拦截，避免进入 errorThrower 再处理）
  responseInterceptors.push((response: Record<string, unknown>) => {
    const data = response?.data as
      | {
          code?: string | number;
          errorCode?: string | number;
        }
      | undefined;

    if (data && typeof data === 'object') {
      const respCode = data.code ?? data.errorCode;
      if (respCode !== undefined && isUnauthorized(respCode)) {
        errorCfg.onUnauthorized();
        // 添加标记，防止 errorHandler 再次调用 onUnauthorized
        const error: UnauthorizedError = new Error(
          `Unauthorized (${respCode})`,
        );
        error._unauthorizedHandled = true;
        throw error;
      }
    }

    return response;
  });

  // 构建配置
  const config: Record<string, unknown> = {
    timeout,
    requestInterceptors,
    responseInterceptors,
    errorConfig: {
      errorThrower: createErrorThrower(errorCfg.successCode),
      errorHandler: createErrorHandler(errorCfg),
    },
  };

  if (baseURL !== undefined) {
    config.baseURL = baseURL;
  }

  // 加密启用时强制 responseType:'text'，阻止 axios 自动 JSON.parse，
  // 由解密拦截器统一处理解密和 JSON 解析
  if (encryption?.enabled && encryption.strategy) {
    config.responseType = 'text';
  } else if (responseType !== undefined) {
    config.responseType = responseType;
  }

  return config;
}
